/**
 * User Info Composable
 * 使用者資訊管理
 * 原始來源: machines/user_info.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/user/userInfo.js)
 * - 業務邏輯：保留在 composable 中
 * - 單例模式：使用 nuxtApp._userInfoContext 避免閉包累積
 */

import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { userApi, activityApi } from '@/api'
import { useUserInfoStore } from '@/stores/user/userInfo'

export const useUserInfo = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保閉包只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._userInfoContext) {
    const { $request } = nuxtApp
    const { appSecret } = useRuntimeConfig().public

    const store = useUserInfoStore()

    // API Methods - 只創建一次
    const fetchUserInfo = async () => {
      store.setStatus('fetching')
      store.setError(null)

      try {
        const response = await $request.send(userApi.getUserInfo())
        const data = response.data || response

        store.setUserInfo(data || {})
        store.setStatus('idle')
        return data
      } catch (err) {
        store.setError(err)
        store.setStatus('idle')
        throw err
      }
    }

    const updateUserInfo = async (data) => {
      store.setStatus('updating')
      store.setError(null)

      try {
        const response = await $request.send(userApi.updateUserInfo(data))
        const result = response.data || response

        // Note: 因為更新 userInfo 後 get self 還是拿到舊資料
        // 所以這裡不重新 fetch，而是直接更新 state
        // 如果需要最新資料，可以手動呼叫 fetchUserInfo

        store.setStatus('idle')
        return result
      } catch (err) {
        store.setError(err)
        store.setStatus('idle')
        throw err
      }
    }

    const setUserInfo = (data) => {
      store.setUserInfo(data)
    }

    const fetchLevelBonusInfo = async () => {
      const { data } = await $request.send(activityApi.getLevelBonusInfo({ appSecret }))
      store.setLevelBonusInfo(data)
    }

    const fetchProfile = async () => {
      store.setStatus('fetchingProfile')
      store.setError(null)

      try {
        const response = await $request.send(userApi.getUserProfile())
        const data = response.data || response

        store.setProfileGroup(data || {})
        store.setStatus('idle')
        return data
      } catch (err) {
        store.setError(err)
        store.setStatus('idle')
        throw err
      }
    }

    // 從 store 解構的 refs
    const storeRefs = storeToRefs(store)

    nuxtApp._userInfoContext = {
      // Store refs
      status: storeRefs.status,
      userInfo: storeRefs.userInfo,
      profileGroup: storeRefs.profileGroup,
      error: storeRefs.error,
      loading: storeRefs.loading,
      isFetching: storeRefs.isFetching,
      isUpdating: storeRefs.isUpdating,
      getUserInfo: storeRefs.getUserInfo,
      hasUserInfo: storeRefs.hasUserInfo,
      isVerified: storeRefs.isVerified,
      isSettleEnable: storeRefs.isSettleEnable,

      // Computed - 只創建一次
      uidSite: computed(() => storeRefs.userInfo.value?.uid_site),
      userAvatar: computed(() => {
        const { photo } = storeRefs.userInfo.value || {}
        const { user } = typeof window !== 'undefined' ? window?.Telegram?.WebApp?.initDataUnsafe || {} : {}
        return user?.photo_url || photo
      }),
      userNickname: computed(() => {
        const { nickname } = storeRefs.userInfo.value || {}
        const { user } = typeof window !== 'undefined' ? window?.Telegram?.WebApp?.initDataUnsafe || {} : {}
        return user?.username || nickname
      }),

      // Methods
      fetchUserInfo,
      updateUserInfo,
      setUserInfo,
      fetchLevelBonusInfo,
      fetchProfile,

      // cleanup 方法：供 SSR cleanup plugin 調用（此 composable 無 watch 需清理）
      cleanup: () => {},
    }
  }

  return nuxtApp._userInfoContext
}
